import React, { useState } from 'react';
import theme from '../../../theme/globalTheme';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Select,
  Checkbox,
  Space,
  Typography,
  Divider
} from 'antd';
import { MailOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

dayjs.locale('fr');

function CashSheetEmailSender() {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [includeInvoices, setIncludeInvoices] = useState(false);

  // Liste des prénoms
  const PRENOM_LIST = ['Pierre', 'Laurie', 'Félicie', 'Gladys', 'Margaux', 'Clem'];

  // URLs des Google Sheets
  const CASH_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1EL4ekPMa26H-AeoiyUl30lwz1JGCLM1Wlc1Iingda5A/edit?gid=0#gid=0&fvid=49069';
  const INVOICES_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1IH352Sy0Ip7iKc1k11Chpe-F-eqFTlahapTdzq8zo48/edit';

  // Webhook Make
  const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/2k7cytetu7yfwvf1s2nqvpvw94myr51u';

  const handleSendEmail = async (values) => {
    setSending(true);

    try {
      // Formater la date du jour
      const todayDate = dayjs().format('DD/MM/YYYY');

      // Créer le message personnalisé
      const emailMessage = `Bonsoir,\n\nCi-joint la feuille de caisse en date du ${todayDate}${includeInvoices ? ' ainsi que les factures' : ''}.\n\nBonne soirée,\n${values.prenom}`;

      // Préparer les données pour le webhook
      const webhookData = {
        recipient_email_1: values.email1,
        recipient_email_2: values.email2,
        subject: `Feuille de caisse - ${todayDate}`,
        message: emailMessage,
        cash_sheet_url: CASH_SHEET_URL,
        invoices_sheet_url: includeInvoices ? INVOICES_SHEET_URL : null,
        include_invoices: includeInvoices,
        sender_name: values.prenom,
        date: todayDate
      };

      console.log('Sending webhook data:', webhookData);

      // Envoyer au webhook Make
      const response = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        throw new Error(`Webhook error: ${response.status}`);
      }

      // Make peut retourner "Accepted" en texte ou JSON
      const contentType = response.headers.get('content-type');
      let result;

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        result = await response.text();
      }

      console.log('Webhook response:', result);

      message.success(`Email envoyé avec succès à ${values.email1} et ${values.email2} !`);

      // Optionnel: réinitialiser certains champs
      // form.resetFields(['prenom']);

    } catch (error) {
      console.error('Error sending email:', error);
      message.error(`Erreur lors de l'envoi de l'email: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  padding: '2rem' }}>
      <Card>
        <Title level={2}>
          <MailOutlined /> Envoi Automatique Feuille de Caisse
        </Title>
        <Text type="secondary">
          Envoyez automatiquement la feuille de caisse (et optionnellement les factures) par email.
        </Text>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSendEmail}
          initialValues={{
            email1: '',
            email2: '',
            prenom: 'Pierre'
          }}
        >
          <Title level={4}>Destinataires</Title>

          <Form.Item
            name="email1"
            label="Email Destinataire 1"
            rules={[
              { required: true, message: 'Email requis' },
              { type: 'email', message: 'Email invalide' }
            ]}
          >
            <Input
              placeholder="exemple@masterski.com"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="email2"
            label="Email Destinataire 2"
            rules={[
              { required: true, message: 'Email requis' },
              { type: 'email', message: 'Email invalide' }
            ]}
          >
            <Input
              placeholder="exemple2@masterski.com"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Divider />

          <Title level={4}>Paramètres du message</Title>

          <Form.Item
            name="prenom"
            label="Votre prénom"
            rules={[{ required: true, message: 'Prénom requis' }]}
          >
            <Select placeholder="Sélectionnez votre prénom">
              {PRENOM_LIST.map(prenom => (
                <Option key={prenom} value={prenom}>{prenom}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={includeInvoices}
              onChange={(e) => setIncludeInvoices(e.target.checked)}
            >
              Inclure les factures
            </Checkbox>
          </Form.Item>

          <Divider />

          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.glassLight,
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <Text strong>📧 Aperçu du message:</Text>
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: theme.colors.glassMedium,
              borderRadius: '4px',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '13px'
            }}>
              {`Bonsoir,

Ci-joint la feuille de caisse en date du ${dayjs().format('DD/MM/YYYY')}${includeInvoices ? ' ainsi que les factures' : ''}.

Bonne soirée,
${form.getFieldValue('prenom') || '[Prénom]'}`}
            </div>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: theme.colors.glassMedium,
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <Text strong>📎 Documents joints:</Text>
            <ul style={{ marginTop: '8px', marginBottom: '0' }}>
              <li>
                <a href={CASH_SHEET_URL} target="_blank" rel="noopener noreferrer">
                  Feuille de caisse
                </a>
              </li>
              {includeInvoices && (
                <li>
                  <a href={INVOICES_SHEET_URL} target="_blank" rel="noopener noreferrer">
                    Factures
                  </a>
                </li>
              )}
            </ul>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              size="large"
              loading={sending}
              style={{ minWidth: '200px' }}
            >
              {sending ? 'Envoi en cours...' : 'Envoyer les emails'}
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{
          padding: '12px',
          backgroundColor: '#fffbe6',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          <Text strong>💡 Informations:</Text>
          <ul style={{ marginTop: '8px', marginBottom: '0', lineHeight: '1.8' }}>
            <li>Les emails seront envoyés automatiquement via Make webhook</li>
            <li>La date du jour est automatiquement ajoutée au message</li>
            <li>Les liens vers les Google Sheets seront inclus dans l'email</li>
            <li>Cochez "Inclure les factures" seulement si nécessaire</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}

export default CashSheetEmailSender;
